/**
 * DixonColesRow - Individual fixture row in the Dixon-Coles table
 */

import React from 'react';
import { useOdds } from '../../context/OddsContext';
import type { DixonColesFixture } from '../../types';

interface Props {
  fixture: DixonColesFixture;
  isExpanded: boolean;
  onToggle: () => void;
}

// Format kickoff time
function formatKickoff(utcString: string): { date: string; time: string; full: string } {
  const date = new Date(utcString);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const isTomorrow = date.toDateString() === new Date(now.getTime() + 86400000).toDateString();

  const timeStr = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

  let dateStr: string;
  if (isToday) {
    dateStr = 'Today';
  } else if (isTomorrow) {
    dateStr = 'Tmrw';
  } else {
    dateStr = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  }

  return {
    date: dateStr,
    time: timeStr,
    full: `${dateStr} ${timeStr}`,
  };
}

// Get team abbreviation (3 letters)
function getTeamAbbr(name: string): string {
  // Handle common patterns
  const words = name.split(/\s+/);
  if (words.length === 1) {
    return name.slice(0, 3).toUpperCase();
  }
  // Use first letter of each word (up to 3)
  return words.slice(0, 3).map(w => w[0]).join('').toUpperCase();
}

// Format probability as percentage
function formatProb(p: number | undefined | null): string {
  if (p === undefined || p === null) return '-';
  return `${(p * 100).toFixed(0)}%`;
}

export function DixonColesRow({ fixture, isExpanded, onToggle }: Props) {
  const { formatOdds } = useOdds();
  const probs = fixture.probabilities;
  const odds = fixture.odds;
  const kickoff = formatKickoff(fixture.kickoff_utc);

  // Format odds using user's preferred format
  const fmtOdds = (o: number | undefined | null): string => {
    if (o === undefined || o === null) return '-';
    return formatOdds(o);
  };

  return (
    <tr
      className={`dc-row ${isExpanded ? 'expanded' : ''} ${!fixture.has_dc_data ? 'no-dc-data' : ''}`}
      onClick={onToggle}
    >
      {/* Kickoff */}
      <td className="col-kickoff">
        <span className="kickoff-full">{kickoff.full}</span>
        <span className="kickoff-stacked">
          <span className="kickoff-date">{kickoff.date}</span>
          <span className="kickoff-time">{kickoff.time}</span>
        </span>
      </td>

      {/* Match */}
      <td className="col-match">
        <div className="match-teams">
          {fixture.home_team.logo && (
            <img src={fixture.home_team.logo} alt="" className="team-logo" />
          )}
          <span className="team-name home">{fixture.home_team.name}</span>
          <span className="team-abbr home">{getTeamAbbr(fixture.home_team.name)}</span>
          <span className="vs">vs</span>
          <span className="team-name away">{fixture.away_team.name}</span>
          <span className="team-abbr away">{getTeamAbbr(fixture.away_team.name)}</span>
          {fixture.away_team.logo && (
            <img src={fixture.away_team.logo} alt="" className="team-logo" />
          )}
        </div>
      </td>

      {/* League */}
      <td className="col-league">
        <div className="league-info">
          {fixture.league.logo && (
            <img src={fixture.league.logo} alt="" className="league-logo" />
          )}
          <span className="league-name">{fixture.league.name}</span>
        </div>
      </td>

      {/* Probabilities with odds */}
      <td className="col-prob">
        <div className="prob-cell">
          <span className="prob-value">{formatProb(probs?.home_win)}</span>
          <span className="odds-value">{fmtOdds(odds.home_win)}</span>
        </div>
      </td>
      <td className="col-prob">
        <div className="prob-cell">
          <span className="prob-value">{formatProb(probs?.draw)}</span>
          <span className="odds-value">{fmtOdds(odds.draw)}</span>
        </div>
      </td>
      <td className="col-prob">
        <div className="prob-cell">
          <span className="prob-value">{formatProb(probs?.away_win)}</span>
          <span className="odds-value">{fmtOdds(odds.away_win)}</span>
        </div>
      </td>
      <td className="col-prob hide-tablet">
        <div className="prob-cell">
          <span className="prob-value">{formatProb(probs?.over_1_5)}</span>
          <span className="odds-value">{fmtOdds(odds.over_1_5)}</span>
        </div>
      </td>
      <td className="col-prob">
        <div className="prob-cell">
          <span className="prob-value">{formatProb(probs?.over_2_5)}</span>
          <span className="odds-value">{fmtOdds(odds.over_2_5)}</span>
        </div>
      </td>
      <td className="col-prob hide-tablet">
        <div className="prob-cell">
          <span className="prob-value">{formatProb(probs?.over_3_5)}</span>
          <span className="odds-value">{fmtOdds(odds.over_3_5)}</span>
        </div>
      </td>
      <td className="col-prob">
        <div className="prob-cell">
          <span className="prob-value">{formatProb(probs?.btts_yes)}</span>
          <span className="odds-value">{fmtOdds(odds.btts_yes)}</span>
        </div>
      </td>

      {/* Expand toggle */}
      <td className="col-expand">
        <svg
          className={`expand-icon ${isExpanded ? 'expanded' : ''}`}
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="currentColor"
        >
          <path d="M4 6l4 4 4-4" />
        </svg>
      </td>
    </tr>
  );
}
